import React, {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from 'react';

export type MeetingOpenElsewhereContextValue = {
  /** Meeting-level signal: another tab holds the same LiveKit identities. */
  reportDuplicateIdentity: (surface: string) => void;
  openElsewhere: boolean;
  /** Clears the flag and bumps session generation so tokens remint on remount. */
  useThisWindow: () => void;
  sessionGeneration: number;
  /** When true, server-closed room / removed participant stops reconnect (lecturer default). */
  stopOnRoomClosed: boolean;
};

const MeetingOpenElsewhereContext = createContext<MeetingOpenElsewhereContextValue | null>(null);

export function MeetingOpenElsewhereProvider({
  children,
  stopOnRoomClosed = true,
}: {
  children: React.ReactNode;
  stopOnRoomClosed?: boolean;
}) {
  const [openElsewhere, setOpenElsewhere] = useState(false);
  const [sessionGeneration, setSessionGeneration] = useState(0);
  const reportedRef = useRef(false);

  // surface is logged in S3 breadcrumbs; kept for call-site honesty.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const reportDuplicateIdentity = useCallback((surface: string) => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    setOpenElsewhere(true);
  }, []);

  const useThisWindow = useCallback(() => {
    reportedRef.current = false;
    setOpenElsewhere(false);
    setSessionGeneration((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({
      reportDuplicateIdentity,
      openElsewhere,
      useThisWindow,
      sessionGeneration,
      stopOnRoomClosed,
    }),
    [reportDuplicateIdentity, openElsewhere, useThisWindow, sessionGeneration, stopOnRoomClosed],
  );

  return (
    <MeetingOpenElsewhereContext.Provider value={value}>
      {children}
    </MeetingOpenElsewhereContext.Provider>
  );
}

export function useMeetingOpenElsewhere(): MeetingOpenElsewhereContextValue {
  const ctx = useContext(MeetingOpenElsewhereContext);
  if (!ctx) {
    throw new Error('useMeetingOpenElsewhere requires MeetingOpenElsewhereProvider');
  }
  return ctx;
}

/** Optional hook for shared meeting components (student path has no provider). */
export function useMeetingOpenElsewhereOptional(): MeetingOpenElsewhereContextValue | null {
  return useContext(MeetingOpenElsewhereContext);
}
